import type { Microsoft365Source } from '@dbrain/shared';
import { Alert, Button, Icons, type IconComponent } from '@dbrain/ui';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMicrosoft365DevicePoll, type Microsoft365DeviceStart } from '../../../api/microsoft365.js';
import { useMicrosoft365FeatureConnect } from '../../../api/microsoft365-files.js';
import { CopyButton } from '../../chat/Markdown.js';
import { ErrorAlert } from '../../ErrorAlert.js';
import { ConsentRequestButton } from '../parts.js';
import { openable, reasonKey } from '../state.js';
import { featuresFor, type Access } from './model.js';

type PanelAccess = Exclude<Access, 'ready'>;

const TONE: Record<PanelAccess, 'info' | 'warning' | 'neutral'> = {
  connect: 'info',
  reconnect: 'warning',
  consent: 'warning',
  scopes: 'warning',
  off: 'neutral',
  unavailable: 'neutral',
};

const ICON: Record<PanelAccess, IconComponent> = {
  connect: Icons.connections,
  reconnect: Icons.regenerate,
  consent: Icons.shield,
  scopes: Icons.key,
  off: Icons.lock,
  unavailable: Icons.info,
};

/**
 * What the picker shows instead of the files (MsDurumlar's words; the picker's panels have no mockup): "Bunun için
 * Microsoft hesabınızı bağlayın", the expired connection, the admin's consent, "Yeni izin gerekiyor · yeniden
 * bağlan", turned off, Microsoft unreachable. Connecting names the source's feature, so Microsoft asks its permission
 * in the same sign-in, and comes back to `returnTo`, which reopens the picker on the same source. A firm that signs in
 * by code gets the code here; once signed in, the files are listed. No link leaves Oppy from here: Microsoft's page
 * opens from a button.
 */
export function ConsentPanel({
  access,
  source,
  returnTo,
  returned,
  onRetry,
}: {
  access: PanelAccess;
  source: Microsoft365Source;
  returnTo: string;
  /** a sign-in that came back with an error (`?microsoft365=error&reason=…`) */
  returned?: string | null;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const connect = useMicrosoft365FeatureConnect();
  const [device, setDevice] = useState<Microsoft365DeviceStart | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [badUrl, setBadUrl] = useState(false);
  const poll = useMicrosoft365DevicePoll(device);
  const retry = useRef(onRetry);
  retry.current = onRetry;
  const Icon = ICON[access];

  const start = () => {
    setBadUrl(false);
    setDevice(null);
    connect.mutate(
      { returnTo, features: featuresFor(source) },
      {
        onSuccess: (result) => {
          if (result.kind === 'device') {
            setDevice({ id: result.id, userCode: result.userCode, verificationUri: result.verificationUri, expiresAt: result.expiresAt, interval: result.interval });
            return;
          }
          if (!openable(result.url)) {
            setBadUrl(true);
            return;
          }
          // the button stays busy while the browser leaves for Microsoft's page
          setLeaving(true);
          window.location.assign(result.url);
        },
      },
    );
  };

  // signed in with the code: the state was read again; the view is asked for again
  useEffect(() => {
    if (poll.status === 'connected') retry.current();
  }, [poll.status]);

  const title =
    access === 'off' ? t(`microsoft365.files.access.off.title.${source}`) : t(`microsoft365.files.access.${access}.title`);
  const text = access === 'scopes' ? t(`microsoft365.files.access.scopes.text.${source}`) : t(`microsoft365.files.access.${access}.text`);
  const signIn = access === 'connect' || access === 'reconnect' || access === 'scopes';

  return (
    <section className={`m365-access m365-access--${TONE[access]}`} aria-labelledby={titleId} role="status" data-testid="m365-picker-access" data-access={access}>
      <span className="m365-access__icon" aria-hidden="true">
        <Icon size={18} />
      </span>
      <div className="m365-access__body">
        <h3 id={titleId} className="m365-access__title">
          {title}
        </h3>
        <p className="m365-access__text">{text}</p>
        {returned ? (
          <Alert tone="error" title={t('microsoft365.files.access.returned', { reason: t(`microsoft365.user.reasons.${reasonKey(returned)}`) })} data-testid="m365-picker-returned" />
        ) : null}
        {device ? <DeviceStep device={device} status={poll.status} reason={poll.reason} onAgain={start} busy={connect.isPending} /> : null}
        {badUrl ? <Alert tone="error" title={t('microsoft365.files.access.badUrl')} /> : null}
        <ErrorAlert error={connect.error ?? poll.error} />
        {device ? null : (
          <div className="m365-access__actions">
            {signIn ? (
              <Button size="sm" icon={access === 'connect' ? undefined : <Icons.regenerate size={15} />} busy={connect.isPending || leaving} onClick={start} data-testid="m365-picker-connect">
                {t(`microsoft365.files.access.${access}.action`)}
              </Button>
            ) : access === 'consent' ? (
              <ConsentRequestButton />
            ) : access === 'unavailable' ? (
              <Button size="sm" variant="secondary" icon={<Icons.regenerate size={15} />} onClick={onRetry} data-testid="m365-picker-retry">
                {t('microsoft365.files.access.unavailable.action')}
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}

/** The device code (a firm that signs in by code): the code with "Kopyala", Microsoft's page from a button, waiting. */
function DeviceStep({ device, status, reason, onAgain, busy }: { device: Microsoft365DeviceStart; status: string; reason: string | null; onAgain: () => void; busy: boolean }) {
  const { t } = useTranslation();
  if (status === 'pending') {
    return (
      <div className="m365-access__device" data-testid="m365-picker-device">
        <p>{t('microsoft365.files.access.device.intro')}</p>
        <div className="m365-access__code-row">
          <code className="m365-device__code" data-testid="m365-picker-device-code">
            {device.userCode}
          </code>
          <CopyButton text={device.userCode} />
        </div>
        <div className="m365-access__actions">
          {openable(device.verificationUri) ? (
            <Button size="sm" icon={<Icons.external size={15} />} onClick={() => window.open(device.verificationUri, '_blank', 'noopener,noreferrer')} data-testid="m365-picker-device-open">
              {t('microsoft365.files.access.device.open')}
            </Button>
          ) : null}
          <span className="m365-access__wait" role="status">
            <span className="ui-spinner ui-spinner--inline" aria-hidden="true" />
            {t('microsoft365.files.access.device.waiting')}
          </span>
        </div>
      </div>
    );
  }
  if (status === 'connected') return null;
  return (
    <div className="m365-access__device" data-testid="m365-picker-device" data-status={status}>
      <p>
        {status === 'expired'
          ? t('microsoft365.files.access.device.expired')
          : status === 'declined'
            ? t('microsoft365.files.access.device.declined')
            : t(`microsoft365.user.reasons.${reasonKey(reason)}`)}
      </p>
      <div className="m365-access__actions">
        <Button size="sm" variant="secondary" icon={<Icons.regenerate size={15} />} busy={busy} onClick={onAgain} data-testid="m365-picker-device-again">
          {t('microsoft365.files.access.device.again')}
        </Button>
      </div>
    </div>
  );
}
